import { CreatePlatformUserBody, PlatformUser, PlatformUserStatus, Salutation, TrialEligibilityDTO, UserWorkspaceMembershipList } from 'models/user'
import { EntityKeys, QueryType, getMutation } from 'utils/reactQuery'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import Cookies from 'js-cookie'
import { Language } from 'translations/Language'
import { RoleMimetype } from 'constants/API'
import { InstructionOptionDTO } from 'models/purchaseFlow'
import { logAnalyticsEvent } from 'utils/analytics'
import { isRateLimitError } from 'utils/APIDecideErrorType'
import { logger } from 'utils/logging'
import { useAPI } from 'utils/API'
import { useAuth0 } from 'utils/auth'

export enum UserQueryKeys {
  AVAILABLE_BILLING_PRODUCTS = 'AVAILABLE_BILLING_PRODUCTS',
  STRIPE_PAYMENT_METHOD = 'STRIPE_PAYMENT_METHOD'
}

enum Endpoints {
  GET_USER_AVAILABLE_BILLING_PRODUCTS = '/user/availableBillingProducts',
  GET_USER_CLIENT = '/user/client',
  GET_USER_WORKSPACES = '/user/workspace',
  SET_PROPSTACK_BANNER = '/user/workspace/{workspaceId}/propstack-banner',
  GET_TRIAL_ELIGIBILITY = '/users/me/trial-eligibility',
  RESEND_CHANGE_EMAIL = '/user/me/email/resendVerification',
  RESET_PASSWORD = '/user/resetPassword',
  PASSWORD_RESET = '/user/password-reset',
  TOGGLE_CT_STATUS = '/user/me/enabled',
  UPDATE_RECEIPT_EMAIL = '/user/billingEmail?billingEmail={billingEmail}',
  UPDATE_PREFERRED_PAYMENT_METHOD = '/user/preferredPaymentMethod?preferredPaymentMethod={paymentMethod}',
  UPDATE_USER_ME = '/user/',
  UPDATE_VAT_NUMBER = '/user/vatNumber?vatNumber={vatNumber}',
  USER_CHANGE_LOCALE = '/user/locale?email={email}',
  UPDATE_PREFERRED_WORKSPACE = '/user/me/preferredWorkspace',
  UPDATE_USER_CC_EMAILS = '/user/ccEmails',
  USER_CHANGE_EMAIL = '/user/me/email',
  USER_CREATE = '/user/',
  USER_DELETE_SAVED_STRIPE_PAYMENT_METHODS = '/user/savedPaymentMethods/{stripeId}',
  USER_ME = '/user/me',
  USER_VERIFY = '/user/verify',
  USER_VERIFY_EMAIL = '/user/verifyEmail?token={token}',
  USER_STATUS = '/user/status'
}

// QUERIES

/** /user/me retry backoff (300 → 600 → 1200 ms): three retries cost ~2 s while the startup loader waits on them */
export const USER_ME_RETRY_BASE_DELAY_MS = 300
export const USER_ME_RETRY_MAX_DELAY_MS = 1200

/** Delay before retry number `attemptIndex + 1` (React Query passes the 0-based failure count) */
export const userMeRetryDelay = (attemptIndex: number) => Math.min(USER_ME_RETRY_BASE_DELAY_MS * 2 ** attemptIndex, USER_ME_RETRY_MAX_DELAY_MS)

export function useUserMe(enabled = true) {
  const api = useAPI<Endpoints>()
  const { roles } = useAuth0()

  // A new user has no role until provisioned (POST /user/), which would resolve the Accept
  // header to plain application/json. Fall back to the client mimetype so /user/me
  // deterministically 404s for unprovisioned users and the signup flow can kick in — parity
  // with the pre-ISSUE-1119 userMe saga (ISSUE-2266).
  const roleNotSet = !roles || roles.all.size === 0

  return useQuery<PlatformUser | null>({
    queryKey: [EntityKeys.USER_ME, QueryType.GET],
    queryFn: () => api.get<PlatformUser | null>(
      Endpoints.USER_ME,
      {},
      false,
      roleNotSet ? { headers: { Accept: RoleMimetype.CLIENT } } : undefined
    ),
    enabled,
    retry: (failureCount, error: any) => {
      // Only a network error or a 5xx can succeed on a retry. Any 4xx answers the same again: 401/403/404 are
      // the signup flow (the pre-ISSUE-1119 saga fetched exactly once), and retrying a 429 only extends the limit.
      const status = error?.response?.status
      return (!status || status >= 500) && failureCount < 3
    },
    retryDelay: userMeRetryDelay,
  })
}

export function useGetUserWorkspaces(enabled = true) {
  const api = useAPI<Endpoints>()

  return useQuery<UserWorkspaceMembershipList | null>({
    queryKey: [EntityKeys.USER_WORKSPACE, QueryType.LIST_MINE],
    queryFn: () => api.get<UserWorkspaceMembershipList | null>(
      Endpoints.GET_USER_WORKSPACES,
      {},
      false
    ),
    enabled,
  })
}

/** Dismisses/restores the Propstack purpose banner for the current user in one workspace (ISSUE-2136). */
export function useSetPropstackBannerDismissal() {
  const api = useAPI<Endpoints>()

  return getMutation<unknown, { workspaceId: string, dismissed: boolean }>(
    ({ workspaceId, dismissed }) => api.put(
      Endpoints.SET_PROPSTACK_BANNER,
      { workspaceId },
      { dismissed },
      false
    ),
    (client, { workspaceId, dismissed }) => {
      // GET /user/workspace is slow for large workspaces (>20s), so invalidating makes the banner
      // wait on the refetch. Write the known value instead so the 204 reflects instantly (ISSUE-2489).
      client.setQueryData<UserWorkspaceMembershipList | null>(
        [EntityKeys.USER_WORKSPACE, QueryType.LIST_MINE],
        (prev) => prev ? { ...prev, workspaces: prev.workspaces?.map((ws) => ws.workspaceId === workspaceId ? { ...ws, propstackBannerDismissed: dismissed } : ws) } : prev
      )
    },
    {
      onError: (error, { workspaceId, dismissed }) => {
        logger.error('Propstack banner dismissal update failed', error, {
          context: { component: 'useSetPropstackBannerDismissal', action: dismissed ? 'dismiss' : 'restore', metadata: { workspaceId } }
        })
      }
    }
  )
}

/**
 * Records the workspace the user switched into so the next login restores it on any browser or device.
 *
 * Deliberately quiet: the switch has already happened in this tab, so a failed write costs only the
 * restore — on another device, and on the next new tab in this browser too, since nothing else persists
 * the choice any more. That is not worth an error toast interrupting the switch that did succeed.
 *
 * No `onError` handler either: `API.logError` already reports every failed request to Sentry fingerprinted
 * by endpoint and status, so a handler here would double-report each failure and would have to hand the
 * raw `AxiosError` to the logger as context — the bearer-token/PII leak ISSUE-2094 removed elsewhere.
 *
 * No cache write: session storage already reflects the selection immediately, so the cached profile's
 * stale value can never win the resolution order.
 */
export function useUpdatePreferredWorkspace() {
  const api = useAPI<Endpoints>()

  return getMutation<unknown, { workspaceId: string }>(
    ({ workspaceId }) => api.put(
      Endpoints.UPDATE_PREFERRED_WORKSPACE,
      {},
      { workspaceId },
      false
    )
  )
}

export function useUserStatus(email?: string) {
  const api = useAPI<Endpoints>()

  return useQuery<PlatformUserStatus | null>({
    queryKey: [EntityKeys.USER_STATUS, QueryType.GET],
    queryFn: () => api.get<PlatformUserStatus | null>(
      Endpoints.USER_STATUS,
      {},
      false,
      {
        params: {
          email
        },
        headers: {
          Accept: email ? RoleMimetype.ADMIN : RoleMimetype.CLIENT
        }
      }
    )
  })
}

/** Whether the current user is still eligible for a subscription trial (e.g. PRO 1-month trial). */
export function useGetTrialEligibility(enabled = true) {
  const api = useAPI<Endpoints>()

  return useQuery<TrialEligibilityDTO | null>({
    queryKey: [EntityKeys.TRIAL_ELIGIBILITY, QueryType.GET],
    queryFn: async () => {
      try {
        return await api.get<TrialEligibilityDTO | null>(
          Endpoints.GET_TRIAL_ELIGIBILITY,
          {},
          false
        )
      } catch (error) {
        // Surface trial-eligibility outages in Sentry: consumers only read `data`, so a
        // failure would otherwise silently degrade to trialAllowed=false with no signal
        logger.error('Failed to fetch trial eligibility', error as Error, {
          context: {
            component: 'useGetTrialEligibility',
          }
        })

        throw error
      }
    },
    enabled,
  })
}

export function useGetAvailableBillingProducts(enabled?: boolean) {
  const api = useAPI<Endpoints>()

  return useQuery({
    queryKey: [EntityKeys.USER_WORKSPACE, QueryType.GET, UserQueryKeys.AVAILABLE_BILLING_PRODUCTS],
    queryFn: () => api.get<InstructionOptionDTO[]>(
      Endpoints.GET_USER_AVAILABLE_BILLING_PRODUCTS,
      {},
      false
    ),
    enabled,
  })
}

export function useUserMeAlternative(enabled = false) {
  const api = useAPI<Endpoints>()

  return useQuery<PlatformUser | null>({
    queryKey: [EntityKeys.USER_ME, QueryType.GET, 'alternative'],
    queryFn: () => api.get<PlatformUser | null>(
      Endpoints.USER_ME,
      {},
      false,
      { headers: { Accept: RoleMimetype.CLIENT } }
    ),
    enabled,
    retry: false,
  })
}

export function useUpdateUserMe() {
  const api = useAPI<Endpoints>()
  const queryClient = useQueryClient()

  return useMutation<AxiosResponse<PlatformUser>, Error, { body: Partial<PlatformUser> }>({
    mutationFn: ({ body }) => api.put(
      Endpoints.UPDATE_USER_ME,
      {},
      body,
      true
    ),
    onSuccess: (data) => {
      queryClient.setQueryData([EntityKeys.USER_ME, QueryType.GET], data.data)
    },
  })
}

// MUTATIONS
export function useGetUserClient$() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<PlatformUser>, { email: string }>(
    ({ email }) => api.get(
      Endpoints.GET_USER_CLIENT,
      {},
      true,
      {
        params: {
          email,
        }
      }
    )
  )
}

export const useToggleLoggedInCTStatus = () => {
  const api = useAPI<Endpoints>()

  return getMutation<PlatformUser, { enabled: boolean }>(
    ({ enabled }) => api.put(
      Endpoints.TOGGLE_CT_STATUS,
      {},
      undefined,
      false,
      { params: { value: enabled } }
    ),
    (client, _variables, data) => {
      client.setQueryData([EntityKeys.USER_ME, QueryType.GET], data)
    }
  )
}

export const useChangeEmail = () => {
  const api = useAPI<Endpoints>()

  return getMutation<{}, { email: string }>(
    ({ email }) => api.post(
      Endpoints.USER_CHANGE_EMAIL,
      {},
      { email },
      false,
      undefined,
      // 429 is the backend's email-change rate limit, an expected outcome the UI explains to the user
      { ignoreErrorFactory: isRateLimitError }
    )
  )
}

export const useResendChangeEmail = () => {
  const api = useAPI<Endpoints>()

  return getMutation(
    () => api.post(
      Endpoints.RESEND_CHANGE_EMAIL,
      {},
      {},
      false,
      undefined,
      { ignoreErrorFactory: isRateLimitError }
    )
  )
}

export const useVerifyEmail = () => {
  const api = useAPI<Endpoints>()

  // Public endpoint: called without the Authorization header, otherwise a stale SPA token 401s it (the
  // link is opened from a mail client with no live session). Identity comes from the token query param.
  return getMutation<{}, { token: string }>(
    ({ token }) => api.postPublic(
      Endpoints.USER_VERIFY_EMAIL,
      { token },
      {}
    )
  )
}

export function useUpdateReceiptEmail() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<PlatformUser>, { email: string, callUpdateUserMe: boolean }>(
    ({ email }) => api.put(
      Endpoints.UPDATE_RECEIPT_EMAIL,
      { billingEmail: email },
      {},
      true
    ),
    (client, { callUpdateUserMe }) => {
      if (callUpdateUserMe) {
        client.invalidateQueries({ queryKey: [EntityKeys.USER_ME] })
      }
    }
  )
}

/** Mutation for resetting password on user profile. */
export function useResetPassword() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<{ status: string }>, {}>(
    () => api.get(
      Endpoints.RESET_PASSWORD,
      {},
      true,
      {
        headers: {
          Accept: RoleMimetype.CLIENT, // TODO: Fix on BE so we can send proper accept header
        }
      }
    )
  )
}

/** Asks for a password-reset e-mail. The backend answers 202 for any address, so the result says nothing about whether the account exists. */
export function useRequestPasswordReset() {
  const api = useAPI<Endpoints>()

  // Identity comes from the e-mail, never the session, so no Authorization header is sent.
  return getMutation<{}, { email: string }>(
    ({ email }) => api.postPublic(Endpoints.PASSWORD_RESET, {}, { email }),
    undefined,
    {
      onError: error => {
        // No token is sent, so a 401 means a missing route or broken permitAll; every global filter drops 401s as an expired session.
        if (error.response?.status === 401) logger.error('Public password-reset endpoint answered 401', undefined, {
          context: { component: 'useRequestPasswordReset', action: 'request_password_reset' },
        })
      },
    }
  )
}

/** Mutation for verifying user profile. */
export function useVerifyUser() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<{ status: string }>, {}>(
    () => api.get(
      Endpoints.USER_VERIFY,
      {},
      true,
      {
        headers: {
          Accept: RoleMimetype.CLIENT,
        }
      }
    )
  )
}

/** Mutation for updating VAT number on user profile. */
export function useUpdateVatNumber() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<PlatformUser>, { vatNumber: string }>(
    ({ vatNumber }) => api.put(
      Endpoints.UPDATE_VAT_NUMBER,
      { vatNumber },
      {},
      true,
      {
        headers: {
          Accept: RoleMimetype.CLIENT, // TODO: Fix on BE so we can send proper accept header
        }
      }
    ), (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.USER_ME] })
    }
  )
}

/** Mutation for updating user cc emails on the user profile. */
export function useUpdateCcEmails() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<PlatformUser>, { ccEmails: string[] }>(
    ({ ccEmails }) => api.put(
      Endpoints.UPDATE_USER_CC_EMAILS,
      {},
      ccEmails,
      true
    ), (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.USER_ME] })
    }
  )
}

interface CreateUserPayload extends CreatePlatformUserBody {
  refreshUserCallback: () => void
  salutation: Salutation
}

/** Mutation for creating a new user (client) account in the system. */
export function useCreateUser() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<PlatformUser>, CreateUserPayload>(
    ({ refreshUserCallback, ...body }) => api.post(
      Endpoints.USER_CREATE,
      {},
      { ...body },
      false,
      {
        // When creating a new user the current role is undefined so we set the client role
        headers: {
          Accept: RoleMimetype.CLIENT, // TODO: Fix on BE so we can send proper accept header
        }
      }
    ),
    (client, { refreshUserCallback }) => {
      client.invalidateQueries({ queryKey: [EntityKeys.USER_ME] })
      // Refresh user on success
      refreshUserCallback()
    },
    {
      // Error or success... doesn't matter!
      onSettled: (data) => {
        logAnalyticsEvent('sign_up', { ...data })
      }
    }
  )
}

export function useChangeLocale() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<PlatformUser>, { email: string, language?: Language, timezone?: string }>(
    ({ email, language, timezone }) => api.put(
      Endpoints.USER_CHANGE_LOCALE,
      { email },
      { language: language || null, timezone: timezone || null },
      true
    ),
    (client, _variables, data) => {
      // Use setQueryData instead of invalidateQueries to avoid triggering
      // a background refetch that causes isLoading flash and cascading re-renders
      client.setQueryData([EntityKeys.USER_ME, QueryType.GET], data.data)
    },
    {
      onSuccess: (_, { language }) => {
        if (language) Cookies.set('i18next', language.toUpperCase())
      }
    }
  )
}

/** Shared mutation key so every PreferredPaymentMethodSwitch instance observes the same latest save. */
export const UPDATE_PREFERRED_PAYMENT_METHOD_MUTATION_KEY = ['UPDATE_PREFERRED_PAYMENT_METHOD'] as const

export function useUpdatePreferredPaymentMethod() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<PlatformUser>, { paymentMethod: string, callUpdateUserMe?: boolean }>(
    ({ paymentMethod }) => api.put(
      Endpoints.UPDATE_PREFERRED_PAYMENT_METHOD,
      { paymentMethod },
      {},
      true
    ),
    (client, { callUpdateUserMe }, data) => {
      // Always land the server-persisted user in the cache: the badge falls back to
      // userMe.preferredPaymentMethod once the mutation is GC'd, and the
      // callUpdateUserMe=false path (gallery upsell) never refetches it otherwise.
      client.setQueryData([EntityKeys.USER_ME, QueryType.GET], data.data)
      if (callUpdateUserMe ?? true) {
        client.invalidateQueries({ queryKey: [EntityKeys.USER_ME] })
      }
    },
    { mutationKey: UPDATE_PREFERRED_PAYMENT_METHOD_MUTATION_KEY }
  )
}
